import { gql } from "@apollo/client";
import PlayerAvatar from "atoms/PlayerAvatar";
import { COLOR_BLACK, COLOR_WHITE } from "helpers/colors";
import EventMarker, { EventMarkerFragment } from "molecules/EventMarker";
import React from "react";

import VideoPlayerHeader from "./VideoPlayerHeader";

export const HighlightPlayerHeaderFragment = gql`
  fragment HighlightPlayerHeaderFragment on Event {
    humanName
    isPlayerEvent
    playerNumber
    teamColor
    person {
      id
      fullNameWithAnonymisation
    }
    createdByPerson {
      id
      fullNameWithAnonymisation
    }
    team {
      id
      name
    }
    # same fields as the shot chart: Period has no id, so the cache replaces it whole and warns on a smaller selection
    period {
      name
      number
    }
    ...EventMarkerFragment
  }
  ${EventMarkerFragment}
`;

const AVATAR_SIZE = 40;

// Player name and team name of a highlight, with its event type and period on the right.
// compact: one line of name and event type over the team and counter, with no avatar or period, for mobile's narrow row
// counter: which highlight of the list is showing, eg "3 of 77"
export default function HighlightPlayerHeader({ event, compact = false, counter }) {
  if (!event) return null;

  const personName = (event.person || event.createdByPerson)?.fullNameWithAnonymisation;
  const teamName = event.team?.name;
  const playerNumber = personName && event.playerNumber && `#${event.playerNumber}`;

  if (compact)
    return (
      <VideoPlayerHeader
        title={personName || teamName}
        titleSuffix={[playerNumber, event.humanName].filter(Boolean).join(" · ")}
        subtitle={[personName && teamName, counter].filter(Boolean).join(" · ")}
      />
    );

  return (
    <VideoPlayerHeader
      avatar={
        event.isPlayerEvent ? (
          <PlayerAvatar
            playerNumber={event.playerNumber}
            size={AVATAR_SIZE}
            style={{
              boxSizing: "border-box",
              border: "1px solid rgba(0, 0, 0, 0.2)",
              backgroundColor: COLOR_WHITE,
              color: COLOR_BLACK,
              fontSize: 16,
            }}
          />
        ) : (
          <EventMarker event={event} size={AVATAR_SIZE} />
        )
      }
      // Team events have no player, so the team name moves up to the title
      title={personName || teamName}
      titleSuffix={playerNumber}
      subtitle={personName && teamName}
      aside={event.humanName}
      // the event time we have is its moment in the game video, not the game clock, so only the period shows
      asideSubtitle={event.period?.name}
    />
  );
}
